import { useState } from "react";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { CopyIconButton } from "@/components/common/CopyIconButton";
import { issueRunnerEnrollmentCode, type RunnerEnrollmentCode } from "@/lib/api";
import { formatDateTime } from "@/lib/format-datetime";

const captionStyle = { color: "var(--fg-2)", fontSize: "var(--text-caption)" };

/**
 * 登録用のコードをボタンで発行し、最後に発行したコードをコピーできるように出す。表示しただけでは発行しない。
 * 発行し直しても、使っていない前のコードは期限まで使える。unavailable (空いているライセンスが無い) なら発行させない。
 */
export function RunnerEnrollmentCodeIssuer({ unavailable = false }: { unavailable?: boolean }) {
	const [enrollment, setEnrollment] = useState<RunnerEnrollmentCode | null>(null);
	const [pending, setPending] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const issue = async () => {
		setPending(true);
		setError(null);
		try {
			setEnrollment(await issueRunnerEnrollmentCode());
		} catch (err) {
			setError(err instanceof Error ? err.message : "登録用のコードの発行に失敗しました。");
			throw err;
		} finally {
			setPending(false);
		}
	};

	const handleIssue = () => {
		if (pending || unavailable) return;
		void issue().catch((err: unknown) => {
			console.error("登録用のコードの発行に失敗しました", err);
		});
	};

	return (
		<div
			data-testid="runner-enrollment"
			style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8 }}
		>
			{enrollment && (
				<>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: 8,
							maxWidth: "100%",
							padding: "8px 10px 8px 14px",
							borderRadius: "var(--radius-3)",
							background: "var(--surface-2)",
							boxShadow: "var(--shadow-ring)",
						}}
					>
						<code
							data-testid="runner-enrollment-code"
							className="select-text font-mono"
							style={{
								minWidth: 0,
								color: "var(--fg-1)",
								fontSize: "var(--text-ui)",
								wordBreak: "break-all",
							}}
						>
							{enrollment.code}
						</code>
						<CopyIconButton value={enrollment.code} label="登録用のコードをコピー" />
					</div>
					<p data-testid="runner-enrollment-expires" style={{ ...captionStyle, margin: 0 }}>
						{`${formatDateTime(enrollment.expiresAt)} まで有効 (10 分)。`}1
						台の登録にだけ使えます。このコードだけでは登録できず、確認コードの入力が要ります。
					</p>
				</>
			)}
			<div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
				{error ? (
					<>
						<span
							data-testid="runner-enrollment-error"
							style={{ ...captionStyle, color: "var(--danger)" }}
						>
							{error}
						</span>
						<ConfirmButton
							label="再試行"
							loading={false}
							variant="secondary"
							size="sm"
							onClick={handleIssue}
							data-testid="runner-enrollment-retry"
						/>
					</>
				) : (
					<>
						<ConfirmButton
							label={enrollment ? "新しいコードを発行" : "登録用のコードを発行"}
							loading={pending}
							disabled={unavailable}
							variant={enrollment ? "secondary" : "primary"}
							size="sm"
							onClick={handleIssue}
							data-testid="runner-enrollment-issue"
						/>
						{unavailable ? (
							<span data-testid="runner-enrollment-unavailable" style={captionStyle}>
								空いているライセンスが無いため発行できません (手順 1)。
							</span>
						) : (
							enrollment && (
								<span style={captionStyle}>使っていない前のコードも期限までは使えます。</span>
							)
						)}
					</>
				)}
			</div>
		</div>
	);
}
